import React, { FC, useCallback, useRef, useState } from 'react';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useRouter } from 'next/router';
import AlertTemplate, { AlertTypes } from '@components/Alert/Alert';
import useTranslations from '@hooks/useTranslation';
import ButtonTemplate from '@ui/Button/Button';
import Title, { TitleTagTypes } from '@ui/SectionTitle/Title';
import { EMAIL_REGEX, FBQ_EVENTS, ROUTES } from '../../constants';
import { contactApi } from '../../services/contact.service';
import ContentTemplate from '../../ui/Content/Content';
import {
    Background,
    ErrorMessage,
    Form,
    GlobalErrorMessage,
    Input,
    SuccessMessage,
    TextArea,
    Wrapper,
} from './ContactForm.elements';

export interface ContactFormProps {
    withoutBackground?: boolean;
}

const ContactFormTemplate: FC<ContactFormProps> = ({ withoutBackground }) => {
    const { t } = useTranslations();

    const router = useRouter();

    const nameInputRef = useRef<HTMLInputElement>();
    const emailInputRef = useRef<HTMLInputElement>();
    const phoneInputRef = useRef<HTMLInputElement>();
    const messageInputRef = useRef<HTMLTextAreaElement>();

    const [hasNameError, setHasNameError] = useState(false);
    const [hasEmailError, setHasEmailError] = useState(false);
    const [hasMessageError, setMessageError] = useState(false);
    const [hasSentError, setHasSentError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSent, setIsSent] = useState(false);
    const [token, setToken] = useState(null);

    const handleClick = async () => {
        setHasNameError(false);
        setHasEmailError(false);
        setMessageError(false);
        setHasSentError(false);
        setIsSent(false);

        const hasError = false;

        if (nameInputRef?.current?.value === '') {
            setHasNameError(true);
            return;
        }

        if (emailInputRef?.current?.value === '' || !EMAIL_REGEX.test(emailInputRef?.current?.value)) {
            setHasEmailError(true);
            return;
        }

        if (messageInputRef?.current?.value === '') {
            setMessageError(true);
            return;
        }

        if (hasError) {
            return;
        }

        try {
            setIsLoading(true);

            const data = {
                ...(nameInputRef?.current?.value && { name: nameInputRef?.current?.value }),
                email: emailInputRef?.current?.value,
                ...(phoneInputRef?.current?.value && { phone: phoneInputRef?.current?.value }),
                message: messageInputRef?.current?.value,
                token,
            };

            (window as any)?.fbq?.(
                'track',
                router.pathname.includes(ROUTES.CLASSROOM_RENT) ? FBQ_EVENTS.LEAD : FBQ_EVENTS.CONTACT
            );

            router.pathname.includes(ROUTES.CLASSROOM_RENT)
                ? await contactApi.contactLease(data)
                : await contactApi.contact(data);
            setIsSent(true);
            setIsLoading(false);

            emailInputRef.current.value = '';
            nameInputRef.current.value = '';
            phoneInputRef.current.value = '';
            messageInputRef.current.value = '';
        } catch {
            setHasSentError(true);
        }
    };

    const verifyRecaptchaCallback = useCallback((token) => {
        setToken(token);
    }, []);

    return (
        <Background withoutBackground={withoutBackground}>
            {hasSentError && <AlertTemplate type={AlertTypes.error} message={t('contact.error')} />}
            <ContentTemplate withPadding>
                <Wrapper>
                    <Form>
                        <Title tagType={TitleTagTypes.H3} alignLeft isBlack>
                            {t('contact.contact.title')}
                        </Title>
                        <Input
                            ref={nameInputRef}
                            placeholder={t('contact.form.name.placeholder')}
                            className={`${hasNameError ? 'error' : ''}`}
                        />
                        {hasNameError && <ErrorMessage>{t('contact.form.name.error')}</ErrorMessage>}
                        <Input
                            ref={emailInputRef}
                            placeholder={t('contact.form.email.placeholder')}
                            className={`${hasEmailError ? 'error' : ''}`}
                        />
                        {hasEmailError && <ErrorMessage>{t('contact.form.email.error')}</ErrorMessage>}
                        <Input ref={phoneInputRef} placeholder={t('contact.form.phone.placeholder')} />
                        <TextArea
                            ref={messageInputRef}
                            placeholder={t('contact.form.message.placeholder')}
                            className={`${hasMessageError ? 'error' : ''}`}
                        />
                        {hasMessageError && <ErrorMessage>{t('contact.form.message.error')}</ErrorMessage>}
                        <GoogleReCaptcha onVerify={verifyRecaptchaCallback} />
                        {(hasNameError || hasEmailError || hasMessageError) && (
                            <GlobalErrorMessage>{t('contact.form.error')}</GlobalErrorMessage>
                        )}
                        {!isSent && (
                            <ButtonTemplate isLoading={isLoading} marginTop={30} onClick={handleClick} text="Odeslat" />
                        )}
                        {isSent && <SuccessMessage>{t('contact.form.sent')}</SuccessMessage>}
                    </Form>
                </Wrapper>
            </ContentTemplate>
        </Background>
    );
};

export default ContactFormTemplate;
